import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/shared/ui/molecules/EmptyState';

import type { LessonPlan } from '../../../types/exam';

// TODO: substituir por hooks Kubb quando os endpoints estiverem disponíveis
const lessonPlans: readonly LessonPlan[] = [];
const disciplines: readonly { id: number; name: string }[] = [];

import { Button, Checkbox, SelectInput, SelectInputItem } from '@ads/components-react';

export interface LessonPlanSearchModalProps {
  readonly preselectedDisciplineId?: number | null;
  readonly onSelect: (plans: readonly LessonPlan[]) => void;
  readonly onCancel: () => void;
}

export function LessonPlanSearchModal({
  preselectedDisciplineId,
  onSelect,
  onCancel,
}: LessonPlanSearchModalProps) {
  const { t } = useTranslation('examManagement');
  const [discipline, setDiscipline] = useState(
    preselectedDisciplineId ? String(preselectedDisciplineId) : 'all',
  );
  const [classGroup, setClassGroup] = useState('all');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const filtered = useMemo(() => {
    let result = [...lessonPlans];
    if (discipline !== 'all') {
      result = result.filter(p => String(p.disciplineId) === discipline);
    }
    if (classGroup !== 'all') {
      result = result.filter(p => p.classGroup === classGroup);
    }
    if (status !== 'all') {
      result = result.filter(p => p.status === status);
    }
    return result;
  }, [discipline, classGroup, status]);

  const classGroups = useMemo(() => {
    const set = new Set(lessonPlans.map(p => p.classGroup));
    return Array.from(set);
  }, []);

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleLink = () => {
    const plans = filtered.filter(p => selected.has(p.id));
    onSelect(plans);
  };

  return (
    <div className="flex flex-col gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs">
      <h4 className="ts-subtitle-sm text-ctx-content-title">{t('lessonSearch.title')}</h4>

      {/** Filters */}
      <div className="flex flex-wrap w-full items-end gap-xs">
        <SelectInput
          label={t('lessonSearch.filters.discipline')}
          value={discipline}
          className="flex-1"
          onValueChange={setDiscipline}
          size="md"
        >
          <SelectInputItem value="all" custom={{ root: 'text-nowrap' }}>
            {t('filters.allDisciplines')}
          </SelectInputItem>
          {disciplines.map(d => (
            <SelectInputItem key={d.id} value={String(d.id)} custom={{ root: 'text-nowrap' }}>
              {d.name}
            </SelectInputItem>
          ))}
        </SelectInput>
        <SelectInput
          label={t('lessonSearch.filters.classGroup')}
          value={classGroup}
          className="flex-1"
          onValueChange={setClassGroup}
          size="md"
        >
          <SelectInputItem value="all" custom={{ root: 'text-nowrap' }}>
            {t('lessonSearch.filters.classGroupPlaceholder')}
          </SelectInputItem>
          {classGroups.map(cg => (
            <SelectInputItem key={cg} value={cg} custom={{ root: 'text-nowrap' }}>
              {cg}
            </SelectInputItem>
          ))}
        </SelectInput>
        <SelectInput
          className="flex-1"
          label={t('lessonSearch.filters.status')}
          value={status}
          onValueChange={setStatus}
          size="md"
        >
          <SelectInputItem value="all" custom={{ root: 'text-nowrap' }}>
            {t('lessonSearch.filters.statusAll')}
          </SelectInputItem>
          <SelectInputItem value="active" custom={{ root: 'text-nowrap' }}>
            {t('lessonSearch.filters.statusActive')}
          </SelectInputItem>
          <SelectInputItem value="closed" custom={{ root: 'text-nowrap' }}>
            {t('lessonSearch.filters.statusClosed')}
          </SelectInputItem>
        </SelectInput>
      </div>

      <span className="ts-subtitle-xxs text-ctx-content-base">
        {t('lessonSearch.resultsCount', { count: filtered.length })}
      </span>

      {/** Results */}
      {filtered.length === 0 ? (
        <EmptyState title={t('publications.empty')} />
      ) : (
        <div className="flex max-h-[300px] flex-col gap-mini overflow-auto">
          {filtered.map(plan => (
            <div
              key={plan.id}
              className="flex items-center gap-sm rounded-sm border-hairline border-ctx-layout-border p-xs"
            >
              <Checkbox
                checked={selected.has(plan.id)}
                onCheckedChange={checked => {
                  if (checked !== 'indeterminate') {
                    toggleSelect(plan.id);
                  }
                }}
              />
              <div className="flex flex-1 flex-col gap-nano">
                <span className="ts-paragraph-xs text-ctx-content-base">{plan.title}</span>
                <span className="ts-caption-xxs text-ctx-content-title">
                  {plan.classGroup} — {plan.professorName} — {plan.startDate} / {plan.endDate}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-mini">
        <Button hierarchy="primary" size="md" onClick={handleLink} disabled={selected.size === 0}>
          {t('lessonSearch.linkSelected')}
        </Button>
        <Button hierarchy="secondary" size="md" onClick={onCancel}>
          {t('actions.cancel')}
        </Button>
      </div>
    </div>
  );
}
